import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import { FV } from "@/components/Form/Validation/FormValidators";
import i18n from "@/i18n";
import BFSection from "@/modules/abstract-ui/data/section/BFSection";
import BFCheckbox from "@/modules/abstract-ui/forms/checkbox/BFCheckbox";
import BFChooserSelect from "@/modules/abstract-ui/forms/chooser/BFChooserSelect";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import { useTypedSelector } from "@/redux/hooks";
import classNames from "classnames";
import { FormApi } from "final-form";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { PaymentKind } from "../../RACInterfaces";
import "./ModeOfPaymentFormFields.scss";

interface ModeOfPaymentFormFieldsProps {
  isCreationForm?: boolean;
  formApi: FormApi;
  disableUnit: boolean;
}

const BankTransferPaymentKinds = [PaymentKind.BANK_TRANSFER];
const ModeOfPaymentFormFields = (props: ModeOfPaymentFormFieldsProps) => {
  const unitTypes = useTypedSelector(
    (state) => state.uiConfig.activeApplication.constants?.businessUnits
  );

  return (
    <div className={classNames("mode-of-payment-form-fields")}>
      {props.isCreationForm && (
        <>
          <div className={`__field`}>
            <Field name="unit" validate={FV.compose(FV.required())}>
              {({ input, meta }) => (
                <BFChooserSelect
                  disabled={props.disableUnit}
                  label={i18n.t("rac:ModeOfPayment.unit", "Bereich")}
                  data={UnitStruct.getUnitSelectOptions(unitTypes)}
                  {...input}
                  {...FV.getValidation(meta)}
                />
              )}
            </Field>
          </div>
          <div className={`__field`}>
            <Field name="kind" validate={FV.compose(FV.required())}>
              {({ input, meta }) => (
                <BFChooserSelect
                  label={i18n.t("rac:ModeOfPayment.kind", "Zahlungsart")}
                  data={InvoiceStruct.getPaymentKindOptions()}
                  {...input}
                  {...FV.getValidation(meta)}
                  onChange={(newValue: PaymentKind) => {
                    const oldValue = input.value;

                    // if oldValue and newValue are iban or international, reset payments

                    if (!BankTransferPaymentKinds.includes(newValue)) {
                      props.formApi.mutators.setValue("payments", null);
                    } else if (!BankTransferPaymentKinds.includes(oldValue)) {
                      props.formApi.mutators.setValue("payments", [
                        {
                          ratioOfPayment: 100,
                          daysForPayment: 14,
                        },
                      ]);
                    }
                    input.onChange(newValue);
                  }}
                />
              )}
            </Field>
          </div>
        </>
      )}
      <div className={`__field`}>
        <Field name="identifier" validate={FV.compose(FV.required())}>
          {({ input, meta }) => (
            <BFInput
              label={i18n.t("rac:ModeOfPayment.identifier", "Kürzel")}
              {...input}
              {...FV.getValidation(meta)}
            />
          )}
        </Field>
      </div>
      <div className={`__field`}>
        <Field name="displayName" validate={FV.compose(FV.required())}>
          {({ input, meta }) => (
            <BFInput
              label={i18n.t("rac:ModeOfPayment.displayName", "Bezeichnung")}
              {...input}
              {...FV.getValidation(meta)}
            />
          )}
        </Field>
      </div>

      <div className={`__field`}>
        <Field name="editable" validate={FV.compose(FV.required())}>
          {({ input, meta }) => (
            <BFCheckbox
              checked={input.value}
              onChange={(_, checked) => input.onChange(checked)}
              {...FV.getValidation(meta)}
            >
              {i18n.t("rac:ModeOfPayment.editable", "Frei editierbar")}
            </BFCheckbox>
          )}
        </Field>
      </div>

      <FormFieldValues names={["kind", "editable"]}>
        {([kind, editable]) => {
          if (BankTransferPaymentKinds.includes(kind) && !editable) {
            return <BankTransferFields {...props} />;
          } else {
            return null;
          }
        }}
      </FormFieldValues>
    </div>
  );
};

export default ModeOfPaymentFormFields;

const BankTransferFields = (props: ModeOfPaymentFormFieldsProps) => {
  return (
    <div>
      <FieldArray name="payments">
        {({ fields }) => {
          return (
            <div>
              {fields.map((name, index) => (
                <BFSection
                  className={`margin-top-10`}
                  key={index}
                  small
                  title={
                    fields.length === 1
                      ? i18n.t(
                          "rac:ModeOfPayment.DueDateForPayment",
                          "Zahlungsziel"
                        )
                      : i18n.t("rac:ModeOfPayment.PaymentEntry", "Zahlung") +
                        ` ${index + 1}`
                  }
                  headerAction={
                    fields.length > 1 && (
                      <BFButton
                        appearance="link"
                        size="xs"
                        onClick={() => {
                          const valueRatio = fields.value[index];
                          const result = fields.remove(index);

                          if (result.length === 1) {
                            result[0].ratioOfPayment = 100;
                          } else {
                            // add ratio to ne last element of the array, after deleting the current one
                            result[result.length - 1].ratioOfPayment =
                              result[result.length - 1].ratioOfPayment +
                              valueRatio.ratioOfPayment;
                          }
                          props.formApi.mutators.setValue("payments", result);
                        }}
                      >
                        {i18n.t("rac:ModeOfPayment.remove", "Entfernen")}
                      </BFButton>
                    )
                  }
                >
                  <div className={`payment-entry padding-10`}>
                    {fields.length > 1 && (
                      <div className={`__field`}>
                        <Field
                          name={`${name}.ratioOfPayment`}
                          validate={FV.compose(FV.required())}
                        >
                          {({ input, meta }) => (
                            <BFInput
                              type="number"
                              label={i18n.t(
                                "rac:ModeOfPayment.ratioOnInvoiceAmount",
                                "Anteil der Rechnungssumme"
                              )}
                              prefix="%"
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          )}
                        </Field>
                      </div>
                    )}
                    <div className={`__field`}>
                      <Field name={`${name}.daysForPayment`}>
                        {({ input, meta }) => (
                          <BFInput
                            type="number"
                            label={i18n.t(
                              "rac:ModeOfPayment.daysForPayment",
                              "Fälligkeit bis Zahlung"
                            )}
                            prefix={i18n.t("Global.Labels.days", "Tage")}
                            {...input}
                            {...FV.getValidation(meta)}
                          />
                        )}
                      </Field>
                    </div>
                    <div className={`discounts`}>
                      <FieldArray name={`${name}.discounts`}>
                        {({ fields }) => (
                          <>
                            <div>
                              {fields.length > 0 && (
                                <div className={`__h3 padding-bottom-5`}>
                                  {i18n.t(
                                    "rac:ModeOfPayment.discounts",
                                    "Skonto"
                                  )}
                                </div>
                              )}
                              <div>
                                {fields.map((name, index) => (
                                  <div className={`discount-entry`}>
                                    <Field name={`${name}.discountPercentage`}>
                                      {({ input, meta }) => (
                                        <BFInput
                                          className={`discount-input`}
                                          type="number"
                                          prefix={
                                            i18n.t(
                                              "rac:ModeOfPayment.discount",
                                              "Skonto"
                                            ) +
                                            " " +
                                            (index + 1)
                                          }
                                          textAlign="right"
                                          suffix={"%"}
                                          {...input}
                                          {...FV.getValidation(meta)}
                                        />
                                      )}
                                    </Field>
                                    <Field name={`${name}.daysForPayment`}>
                                      {({ input, meta }) => (
                                        <BFInput
                                          className={`days-input`}
                                          type="number"
                                          prefix={i18n.t(
                                            "Global.Labels.days",
                                            "Tage"
                                          )}
                                          textAlign="left"
                                          removeSuffixPadding
                                          suffix={
                                            <div
                                              className={`padding-right-10 padding-left-10`}
                                            >
                                              <BFButton
                                                appearance="link"
                                                size="xs"
                                                noPadding
                                                onClick={() =>
                                                  fields.remove(index)
                                                }
                                              >
                                                {i18n.t(
                                                  "rac:ModeOfPayment.remove",
                                                  "Entfernen"
                                                )}
                                              </BFButton>
                                            </div>
                                          }
                                          {...input}
                                          {...FV.getValidation(meta)}
                                        />
                                      )}
                                    </Field>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div
                              className={`__flex __justify-center padding-top-10`}
                            >
                              <BFButton
                                appearance="link"
                                size="xs"
                                noPadding
                                onClick={() =>
                                  fields.push({
                                    daysForPayment: 7,
                                    discountPercentage: 2,
                                  })
                                }
                              >
                                {i18n.t(
                                  "rac:ModeOfPayment.addDiscount",
                                  "Skonto hinzufügen"
                                )}
                              </BFButton>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </BFSection>
              ))}
              <div className={`__flex __justify-center padding-top-10`}>
                {fields.length === 1 && (
                  <BFButton
                    appearance="link"
                    size="xs"
                    onClick={() => {
                      const firstValue = fields.value[0];

                      fields.push({
                        ratioOfPayment: 50,
                        daysForPayment: firstValue.daysForPayment * 2,
                      });
                      props.formApi.mutators.setValue(
                        `payments[0].ratioOfPayment`,
                        50
                      );
                    }}
                  >
                    {i18n.t(
                      "rac:ModeOfPayment.splitPayment",
                      "Zahlung aufteilen"
                    )}
                  </BFButton>
                )}
                {fields.length > 1 && (
                  <BFButton
                    appearance="link"
                    size="xs"
                    onClick={() => {
                      const indexOfLastValue = fields.length - 1;
                      const lastValue = fields.value[indexOfLastValue];

                      fields.push({
                        ratioOfPayment: lastValue.ratioOfPayment / 2,
                        daysForPayment: lastValue.daysForPayment * 2,
                      });
                      props.formApi.mutators.setValue(
                        `payments[${indexOfLastValue}].ratioOfPayment`,
                        lastValue.ratioOfPayment / 2
                      );
                    }}
                  >
                    {i18n.t(
                      "rac:ModeOfPayment.addPaymentEntry",
                      "Weitere Zahlung hinzufügen"
                    )}
                  </BFButton>
                )}
              </div>
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
};
